import type { TableCellProps } from '@chakra-ui/react'
import {
  Alert, Box, Button, Flex, Image, Link, ListItem, OrderedList,
  Spinner,
  Stack,
  Table, Tbody, Td, Text, Tr
} from '@chakra-ui/react'
import type {
  InsuranceParamsPropertyDto, OfferPackage,
  OfferProperty
} from 'BrokerTsLib/src/dtos'
import {
  getDigitalSignatureInstructions,
  getDigitalSignatureSecondInstructions
} from 'BrokerTsLib/src/dtos'
import { ContractAcceptationType, Insurer, PackageItemLimitType, Product, Risk, SelectionType } from 'BrokerTsLib/src/enums'
import { formatPrice, priceFormat } from 'BrokerTsLib/src/Utils/formatPrice'
import moment from 'moment'
import { useContext, useEffect, useState } from 'react'
import QRCode from 'react-qr-code'

import { useApp } from '@/hooks/useApp'

import {
  FormSection,
  FormSectionBody,
  FormSectionDescription,
  FormSectionHeader,
  FormSectionTitle,
} from '../../components/FormSection'
import { Loading } from '../../components/Loading'
import { useGetRiskVariants, useSelections } from '../../hooks'
import { useGetInsurance } from '../../hooks/useInsuranceParams'
import { useConfirmContract, useGetIsContractConfirmed } from '../../hooks/useWizard'
import { useInsuranceParamsContext } from '../../layouts/Wizard'
import { meetingRecordContext } from '../../pages/carkulka/wizard/5'
import { getLogo } from '../../utils/companiesLogos'
import { CancelContract } from '../CancelContract'
import { insurerOfferContext } from '../hoc/WithOffer'
import { ReactivateContract } from '../ReactivateContract'

interface RecapTableRowProps extends TableCellProps {
  col1: string | React.ReactNode
  col2?: string | React.ReactNode
  col3?: string | React.ReactNode
  isHighlighted?: boolean
}

export const RecapTableRow = ({ col1, col2, col3, isHighlighted, sx, ...rest }: RecapTableRowProps) => {
  return (
    <Tr sx={{ m: 0, p: 0 }}>
      <Td sx={{ w: '15rem', p: 1, ...sx }}>
        {col1 && <Text sx={{ fontWeight: isHighlighted ? 'semibold' : 'medium' }}>{col1}</Text>}
      </Td>
      <Td sx={{ p: 1, ...sx }}>{col2 && col2}</Td>
      {col3 && (
        <Td sx={{ p: 1, fontWeight: isHighlighted ? 'semibold' : 'normal', textAlign: 'end', ...sx }}>
          {col3}
        </Td>
      )}
    </Tr>
  )
}

export const Recap = () => {
  const { product } = useApp()
  const { data: dataPf } = useSelections(SelectionType.FrekvencePlatby)
  const pfEnum = dataPf ?? []
  const { data: dataDp } = useSelections(SelectionType.ZpusobDigitalnihoPodpisu)
  const dpEnum = dataDp ?? []

  const { insuranceParams: ip, refetchInsuranceParams } = useInsuranceParamsContext()
  const insuranceParams = ip as InsuranceParamsPropertyDto
  const { isLoading, offer: o } = useContext(insurerOfferContext)
  const offer = o as OfferPackage
  const parts = offer?.parts.filter(x => x.included)
  const cat = insuranceParams?.generalParams.contractAcceptationType
  const isContracted = !!insuranceParams?.generalParams.contractNumber
  const isCanceled = !!insuranceParams?.generalParams.contractCanceled

  const offerId = insuranceParams?.generalParams.selectedOfferId

  const {
    data: dataInsurance,
    refetch: refetchInsurance,
    isLoading: isLoadingInsurance,
  } = useGetInsurance(offerId!)
  const insurance = dataInsurance?.data ?? {}

  const onContractChange = () => {
    refetchInsuranceParams()
    refetchInsurance()
  }

  const [contractConfirmInfo, setContractConfirmInfo] = useState<string | null>(null)
  const [contractConfirmed, setContractConfirmed] = useState<boolean>(false)

  const { mutateAsync: confirmContractAsync, isLoading: isConfirmingContract } = useConfirmContract()
  const { refetch: fetchIsContractConfirmed } = useGetIsContractConfirmed(insuranceParams!.generalParams.paramsId!, { enabled: false })

  const { meetingRecord, isLoading: isMRLoading } =
    useContext(meetingRecordContext)

  const dpDescription = dpEnum
    .find((x) => x.id == (meetingRecord?.digitalSignatureType ?? 0))
    ?.description?.toLowerCase()
  let instructions: string[] = []
  let instructions2: string[] = []
  let signatureUrl = insurance?.digitalSignatureUrl
  let signatureSecondUrl = null
  if (offer && meetingRecord?.digitalSignatureType) {
    instructions = getDigitalSignatureInstructions(
      offer.insurerId,
      meetingRecord?.digitalSignatureType,
    )
    instructions2 = getDigitalSignatureSecondInstructions(
      offer.insurerId,
      meetingRecord?.digitalSignatureType,
    )

    if (
      instructions2.length > 0 &&
      signatureUrl &&
      signatureUrl?.indexOf('|') != -1
    ) {
      const urls = signatureUrl!.split('|')
      signatureUrl = urls[0]
      signatureSecondUrl = urls[1]
    }
  }

  const variantPart = insuranceParams.property.variants?.find(x => x.id == offer?.insuranceVariantId)?.variantParts?.find(x => x.risk == Risk.PROPERTY_TYPE)?.riskVariant
  const { data: dataPropertyTypes } = useGetRiskVariants(Risk.PROPERTY_TYPE)

  const complicityPart = parts?.find(x => x.riskId == Risk.SPOL_PROPERTY)
  const complicityFloodPart = parts?.find(x => x.riskId == Risk.SPOL_FLOOD)
  let responsibilityPart = parts?.find(x => x.riskId == Risk.RESPONSIBILITY)
  if (responsibilityPart && responsibilityPart.limit == 0 && product !== Product.LiabilityInsurance) { // někdy je kalkulován pouze limit odpovědnosti nemovitosti
    responsibilityPart = parts?.find(x => x.riskId == Risk.RESPONSIBILITY_REALESTATE)
  }
  else { // pokud není vůbec offer part odpovědnosti:
    if (!responsibilityPart && product !== Product.LiabilityInsurance) {
      responsibilityPart = parts?.find(x => x.riskId == Risk.RESPONSIBILITY_REALESTATE)
    }
  }
  const responsibilityRealtyPart = parts?.find(x => x.riskId == Risk.RESPONSIBILITY_REALESTATE)

  const complicityText = []
  if (complicityPart)
    complicityText.push(priceFormat(complicityPart.limit, null, '0 Kč'))

  if (complicityFloodPart)
    complicityText.push('povodeň ' + priceFormat(complicityFloodPart.limit, null, '0 Kč'))

  const offerProperty = offer as OfferProperty

  const setConfirmCsob = async () => {
    await confirmContractAsync({ paramsId: insuranceParams!.generalParams.paramsId!.toString(), offerId: offerId! })
      .then((result) => {
        if (result.status === 200 && result.data) {
          if (result.data.success) {
            setContractConfirmed(true)
          }
          else {
            setContractConfirmInfo(result.data.error ?? 'Zjištění stavu podepsání smlouvy selhalo')
          }
        }
      })
      .catch((e) =>
        alert('Zjištění stavu podepsání smlouvy selhalo')
      )
  }

  useEffect(() => {
    if (offer && offer.insurerId == Insurer.Csob && cat === ContractAcceptationType.DigitalSignature) {
      fetchIsContractConfirmed().
        then((resp) => {
          if (resp.data?.data?.success) {
            setContractConfirmed(true)
          }
        })
    }
  }, [offer])

  return (
    <FormSection variant={isCanceled ? 'red' : 'default'}>
      <FormSectionHeader>
        <FormSectionTitle>Rekapitulace pojištění</FormSectionTitle>

        {offer &&
          <FormSectionDescription>
            <Image src={getLogo(offer.insurerId!)} alt={offer.insurerNameFull} width="64px" maxH="48px" />
          </FormSectionDescription>
        }
      </FormSectionHeader>

      {!offer && isLoading && <Loading large={true} />}
      {offer &&
        <FormSectionBody>
          <Table variant="unstyled">
            <Tbody>
              {product === Product.LiabilityInsurance &&
                <>
                  <RecapTableRow col1="Typ pojištění" col2="Samostatná odpovědnost" />
                  {responsibilityPart && responsibilityPart.limit != 0 &&
                    <RecapTableRow col1="Odpovědnost občanská" col2={`${priceFormat(responsibilityPart.limit, null, '0 Kč')}`} />
                  }
                  {responsibilityRealtyPart && responsibilityRealtyPart.limit != 0 &&
                    <RecapTableRow col1="Odpovědnost nemovitosti" col2={`${priceFormat(responsibilityRealtyPart.limit, null, '0 Kč')}`} />
                  }
                </>
              }
              {product !== Product.LiabilityInsurance && offerProperty &&
                <>
                  <RecapTableRow col1="Typ pojištění" col2={`${dataPropertyTypes?.find(x => x.id == variantPart)?.name} „${[...new Set([offerProperty.householdPackageName, offerProperty.realtyPackageName].filter(x => !!x))].join(' + ')}”`} />
                  <RecapTableRow col1="Odpovědnost" col2={`${responsibilityPart?.limit ? priceFormat(responsibilityPart.limit, null, '0 Kč') : 'Bez pojištění'}`} />
                </>
              }
              <RecapTableRow col1="Spoluúčast" col2={complicityText.join(', ')} />

              {offer.parts
                .filter(x => x.valid && x.included && !(x.packageItem?.isAddition ?? false) && product !== Product.LiabilityInsurance)
                .map((part, index) => {
                  const limitText = part.limit === 999999999
                   ? 'Neomezeně'
                    : part.limit
                      ? formatPrice({ price: part.limit })
                      : part.packageItem?.limitType === PackageItemLimitType.Limit
                        ? '0 Kč'
                        : 'Ano'

                  return <RecapTableRow key={`${part.riskId}_${part.partId}`}
                    col1={index === 0 ? 'Rozsah pojištění' : ''}
                    col2={part.name}
                    col3={limitText}
                  />
                })}

              <RecapTableRow
                isHighlighted
                col1="Celkem"
                col2={<Text sx={{ fontWeight: 'semibold' }}>{formatPrice({ price: offer.price })}</Text>}
              />
            </Tbody>
          </Table>

          <Table variant="unstyled">
            <Tbody>

              <RecapTableRow col1="Splátky pojistného" col2={pfEnum.find(x => x.id === offer.paymentFrequencyId)?.description} />
              <RecapTableRow col1="Splátka" col2={formatPrice({ price: offer.priceFrequency })} />
              <RecapTableRow col1="Počátek pojištění" col2={moment(new Date(insuranceParams.generalParams.insuranceStartDate!)).format('D. M. YYYY')} />

              {isContracted &&
                <>
                  {/* <RecapTableRow col1="Způsob sjednání" col2={fsEnum.find(x => x.value === insuranceParams?.generalParams.contractAcceptationType?.toString())?.description} /> */}
                  <RecapTableRow col1="Datum sjednání" col2={moment(new Date(insuranceParams.generalParams.contractCreatedDate!)).format('D. M. YYYY HH:mm')} />
                </>
              }

              {isContracted && cat === 2 &&
                <RecapTableRow col1="Splatnost pojistného"
                  col2={insuranceParams?.generalParams.paymentDateSpecial ?
                    insuranceParams.generalParams.paymentDateSpecial :
                    moment(new Date(insuranceParams.generalParams.paymentDate!)).format('D. M. YYYY')} />
              }
              {isCanceled &&
                <RecapTableRow col1="Smlouva byla stornována" col2={`${moment(new Date(insuranceParams.generalParams.contractCanceledDate!)).format('D. M. YYYY HH:mm')}, ${insuranceParams.generalParams.contractCanceledBy}`} />
              }
            </Tbody>
          </Table>

          {isContracted && (cat === 2 || offer.infos) &&
            <Alert colorScheme="orange">
              <Stack direction='column'>
                {cat === ContractAcceptationType.Payment &&
                  <Text>Pokud nebude první pojistné uhrazeno v termínu, PS nevznikne!</Text>}

                {cat === ContractAcceptationType.DigitalSignature && contractConfirmed && (
                  <Text>
                    Smlouva byla digitálně podepsána
                  </Text>
                )}

                {cat === ContractAcceptationType.DigitalSignature &&
                  dpDescription && !contractConfirmed && (
                    <Text>
                      Doplňující informace k podpisu smlouvy - podpis{' '}
                      <Text as="span" sx={{ fontWeight: 600 }}>
                        {dpDescription}
                      </Text>
                    </Text>
                  )}

                {cat === ContractAcceptationType.DigitalSignature &&
                  !!signatureUrl && !contractConfirmed && (
                    <>
                      <Text>
                        Pokud sjednáváte na zařízení s dotykovou obrazovkou
                        (mobil, tablet) můžete přímo přejít do
                        <Link
                          as={'a'}
                          target="_blank"
                          href={signatureUrl}
                          sx={{
                            color: 'primary.500',
                            fontWeight: 'medium',
                            paddingLeft: '5px',
                            textDecoration: 'underline',
                          }}
                        >
                          aplikace pro podepsání{' '}
                          {signatureSecondUrl
                            ? 'předsmluvního dokumentu'
                            : 'smlouvy'}
                        </Link>
                        .
                      </Text>
                      <Text sx={{ pb: 1 }}>
                        V opačném případě naskenujte QR kód a pokračujte dle
                        kroků níže.
                      </Text>
                      <Flex gap={4}>
                        <Box sx={{ maxWidth: '128px' }}>
                          <QRCode
                            value={signatureUrl}
                            viewBox={'0 0 256 256'}
                            level="M"
                            size={256}
                            style={{
                              height: 'auto',
                              maxWidth: '100%',
                              width: '100%',
                            }}
                          />
                        </Box>
                        <Box>
                          <OrderedList>
                            {instructions.map((instruction) => (
                              <ListItem key={instruction}>
                                {instruction}
                              </ListItem>
                            ))}
                          </OrderedList>
                        </Box>
                      </Flex>
                    </>
                  )}

                {cat === ContractAcceptationType.DigitalSignature &&
                  !!signatureSecondUrl && !contractConfirmed && (
                    <>
                      <Text>
                        Dále na zařízení s dotykovou obrazovkou (mobil,
                        tablet) můžete přejděte do
                        <Link
                          as={'a'}
                          target="_blank"
                          href={signatureSecondUrl}
                          sx={{
                            color: 'primary.500',
                            fontWeight: 'medium',
                            paddingLeft: '5px',
                            textDecoration: 'underline',
                          }}
                        >
                          aplikace pro podepsání smlouvy
                        </Link>
                        .
                      </Text>
                      <Text sx={{ pb: 1 }}>
                        Nebo naskenujte QR kód a pokračujte dle kroků níže.
                      </Text>
                      <Flex gap={4}>
                        <Box sx={{ maxWidth: '128px' }}>
                          <QRCode
                            value={signatureSecondUrl}
                            viewBox={'0 0 256 256'}
                            level="M"
                            size={256}
                            style={{
                              height: 'auto',
                              maxWidth: '100%',
                              width: '100%',
                            }}
                          />
                        </Box>
                        <Box>
                          <OrderedList>
                            {instructions2.map((instruction) => (
                              <ListItem key={instruction}>
                                {instruction}
                              </ListItem>
                            ))}
                          </OrderedList>
                        </Box>
                      </Flex>
                    </>
                  )}

                {cat === ContractAcceptationType.DigitalSignature &&
                  !signatureUrl && !contractConfirmed && (
                    <OrderedList>
                      {instructions.map((instruction) => (
                        <ListItem key={instruction}>{instruction}</ListItem>
                      ))}
                    </OrderedList>
                  )}

                {cat === ContractAcceptationType.DigitalSignature &&
                  offer?.insurerId == Insurer.Csob && !contractConfirmed && (
                    <>
                      <Text sx={{ fontWeight: '600' }}>Nakonec je nutno potvrdit podepsání dokumentů tlačítkem níže. Bez tohoto potvrzení nedojde k aktivaci pojistné smlouvy v pojišťovně.</Text>
                      <Flex sx={{ w: '100%', placeContent: 'start', alignItems: 'baseline' }}>
                        <Button variant='outline' colorScheme='green'
                          onClick={() => setConfirmCsob()}
                          sx={{ mr: 4 }}
                        >Potvrdit podepsání {isConfirmingContract && <Spinner />}</Button>

                        {contractConfirmInfo && <Text>{contractConfirmInfo}</Text>}
                      </Flex>
                    </>
                  )}

                {offer.infos && offer.infos.filter(x => x.message).map(x => (
                  <Text key={x.value} dangerouslySetInnerHTML={{ __html: x.message }}></Text>
                ))}
              </Stack>
            </Alert>
          }

          {insurance.cancellationOptions?.canCancel &&
            <CancelContract
              callback={onContractChange}
              insuranceId={insurance.insuranceId!}
            />
          }
          {insurance.cancellationOptions?.canReactivate &&
            <ReactivateContract
              callback={onContractChange}
              insuranceId={insurance.insuranceId!}
            />
          }
        </FormSectionBody>
      }
    </FormSection>
  )
}
